import { useEffect, useRef, useSyncExternalStore } from 'react';
import { sseService } from 'services/SseService';

import { SSEEvent, SSEEventType } from '../constants/sseEvents';

// ─── Connection State Management ──────────────────────────

const connectionListeners = new Set<() => void>();
let currentConnectionState = false;

// Update the local state and notify listeners when the service state changes
sseService.onAny(() => {
  // We check the actual service state
  const newState = sseService.isConnected;
  if (currentConnectionState !== newState) {
    currentConnectionState = newState;
    connectionListeners.forEach((cb) => cb());
  }
});

// ─── Hooks ────────────────────────────────────────────────

/**
 * Core SSE connection hook.
 * Manages the SSE lifecycle tied to authentication state.
 * Should be called once at the app root.
 */
export function useSSEConnection(token?: string) {
  useEffect(() => {
    if (token) {
      sseService.connect();
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        sseService.connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      // We don't necessarily disconnect here if it's the root app,
      // but we do on logout (via destroySSE)
    };
  }, [token]);

  const isConnected = useSyncExternalStore(
    (cb) => {
      connectionListeners.add(cb);
      return () => connectionListeners.delete(cb);
    },
    () => sseService.isConnected,
  );

  return { isConnected };
}

/**
 * Subscribe to a specific SSE event type.
 * Automatically handles cleanup.
 */
export function useSSEEvent<T = any>(
  eventType: SSEEventType,
  handler: (event: SSEEvent<T>) => void,
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const unsub = sseService.on<T>(eventType, (event) => {
      handlerRef.current(event);
    });

    return unsub;
  }, [eventType]);
}

/**
 * Subscribe to ALL SSE events.
 */
export function useSSEEventAll(handler: (event: SSEEvent) => void) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const unsub = sseService.onAny((event) => {
      handlerRef.current(event);
    });

    return unsub;
  }, []);
}

/**
 * Completely destroy the SSE instance (e.g. on logout)
 */
export function destroySSE(): void {
  sseService.dispose();
  currentConnectionState = false;
  connectionListeners.forEach((cb) => cb());
}
